/**
 * Boot screen — VICIN splash + PRESS START.
 * Sequence (compact, ~1.3s total for new users, 200ms for returning):
 *   0.0s: "VICIN" title fades in + scales
 *   0.6s: loading bar appears, fills over 600ms
 *   1.2s: "▶ PULSA START" blinks (only when no prior auth — returning users skip)
 * A or START (or any tap) advances to auth/home. Guarded against double-fire.
 */
import { useEffect, useRef, useState } from 'react';
import { sfx } from '@/lib/audio';
import { authApi } from '@/lib/api';

interface BootScreenProps {
  onStart: () => void;
}

export function BootScreen({ onStart }: BootScreenProps) {
  const returningUser = authApi.isAuthenticated();
  const [phase, setPhase] = useState<'title' | 'loading' | 'ready'>(
    returningUser ? 'ready' : 'title',
  );
  const firedRef = useRef(false);
  const handlersRef = useRef({ onStart });
  handlersRef.current = { onStart };

  useEffect(() => {
    sfx.bootJingle();
    if (returningUser) return; // skip the cinematic for repeat visits
    const t1 = setTimeout(() => setPhase('loading'), 600);
    const t2 = setTimeout(() => setPhase('ready'), 1200);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [returningUser]);

  // A or START advances to the next screen — single-fire guarded
  useEffect(() => {
    const fire = () => {
      if (firedRef.current) return;
      firedRef.current = true;
      handlersRef.current.onStart();
    };
    window.addEventListener('vicin-gb:a', fire);
    window.addEventListener('vicin-gb:start', fire);
    return () => {
      window.removeEventListener('vicin-gb:a', fire);
      window.removeEventListener('vicin-gb:start', fire);
    };
  }, []);

  return (
    <div className="gb-screen">
      <div className="gb-boot">
        {/* Phase 1: VICIN title */}
        <div className="gb-boot-vicin gb-success-pulse">VICIN</div>

        {/* Phase 2: loading bar (skip for returning users) */}
        {!returningUser && phase !== 'title' && (
          <div className="gb-boot-bar">
            <div className="gb-boot-bar-fill" />
          </div>
        )}

        {/* Phase 3: PRESS START */}
        {(returningUser || phase === 'ready') && (
          <div className="gb-boot-start gb-blink">▶ PULSA START</div>
        )}

        {/* Tap anywhere to start */}
        <button
          onClick={() => {
            if (firedRef.current) return;
            firedRef.current = true;
            onStart();
          }}
          aria-label="Empezar"
          style={{
            position: 'absolute',
            inset: 0,
            background: 'transparent',
            border: 0,
            cursor: 'pointer',
          }}
        />
      </div>
    </div>
  );
}